import { useApiClient } from '@m43/core';
import { DexResultsResource } from '@mark43/dex-api';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useBroadcast } from '../../common/broadcast-channel/use-broadcast';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { DexResultsStore } from './dex-results-store';
import { FederatedSearchResource } from '@mark43/cad-api';

export function useSelectDexResults(dexResultsStore: DexResultsStore) {
    const apiClient = useApiClient();
    const { postMessage } = useBroadcast('dex-results');
    const { isFirstResponder } = useApplicationType();

    const readDexResultMutation = useMutation(
        ({
            incomingMessageId,
            outgoingMessageId,
        }: {
            incomingMessageId?: number;
            outgoingMessageId?: number;
        }) => {
            return apiClient
                .request<DexResultsResource.MarkAsRead>({
                    service: 'DEX',
                    method: 'POST',
                    url: 'dex_results/mark_as_read',
                    data: {
                        incomingMessageIds: incomingMessageId ? [incomingMessageId] : [],
                        outgoingMessageIds: outgoingMessageId ? [outgoingMessageId] : [],
                    },
                })
                .then(() => {
                    return { incomingMessageId, outgoingMessageId };
                });
        },
        {
            onSuccess({ incomingMessageId, outgoingMessageId }) {
                if (incomingMessageId) {
                    dexResultsStore.readResult(incomingMessageId);
                    postMessage({
                        type: 'READ_DEX_RESULT',
                        payload: { incomingMessageId },
                    });
                }
                if (outgoingMessageId) {
                    dexResultsStore.readSummary(outgoingMessageId);
                    postMessage({
                        type: 'READ_DEX_RESULTS_SUMMARY',
                        payload: { outgoingMessageId },
                    });
                }
            },
        }
    );
    const selectDexResult = useCallback(
        (incomingMessageId: number) => {
            dexResultsStore.setSelectedResultId(incomingMessageId);
            if (isFirstResponder && dexResultsStore.frDexPageViewMode !== 'RESULTS') {
                dexResultsStore.setFrDexPageViewMode('RESULTS');
            }
            if (
                dexResultsStore.selectedQueryAndResult &&
                !dexResultsStore.selectedQueryAndResult.result.isRead
            ) {
                readDexResultMutation.mutate({ incomingMessageId });
            }
        },
        [readDexResultMutation, dexResultsStore, isFirstResponder]
    );

    const selectDexResultsSummary = useCallback(
        (outgoingMessageId: number) => {
            dexResultsStore.setSelectedSummaryId(outgoingMessageId);
            if (isFirstResponder && dexResultsStore.frDexPageViewMode !== 'RESULTS') {
                dexResultsStore.setFrDexPageViewMode('RESULTS');
            }
            if (
                dexResultsStore.selectedQueryAndSummary &&
                !dexResultsStore.selectedQueryAndSummary.summary.isRead
            ) {
                readDexResultMutation.mutate({ outgoingMessageId });
            }
        },
        [readDexResultMutation, dexResultsStore, isFirstResponder]
    );
    return { selectDexResult, selectDexResultsSummary };
}

export function useReadAllDexQueries() {
    const apiClient = useApiClient();

    return useMutation(() =>
        apiClient.request<FederatedSearchResource.ReadAllQueries>({
            method: 'POST',
            url: 'federated_search/read_all_queries',
            data: {},
        })
    );
}
